/* eslint-disable @typescript-eslint/no-shadow */
import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo, UserRights, UserTypes } from "../../../../shared/types/user-info";
import { isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { UserService } from "../user.service";
import { User } from "../user.type";
import { CreateUserService } from "./create-user.service";

@Component({
  selector: "app-create-user",
  templateUrl: "./create-user.component.html",
  styleUrls: ["./create-user.component.css"],
  providers: [CreateUserService],
})
export class CreateUserComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  loading = false;
  public isRequiredField = isRequiredField;
  currentUser: UserInfo;
  questions: QuestionBase<string | boolean>[];

  constructor(
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private questionsService: CreateUserService,
    private loader: LoadingService,
    private notificationService: NotificationService,
    private router: Router,
    public location: Location,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  ngOnInit(): void {
    this.questionsService
      .generateQuestions({
        currentUser: this.currentUser,
        hidePartner: true,
        hideCompany: true,
        skipPartner: true,
        skipCompany: true,
        partner: this.currentUser.isPartnerAdmin() ? this.currentUser.partner : null,
        isEdit: false,
        ...(this.currentUser.isCompany() && {
          hideCompany: false,
          skipCompany: false,
          partner: this.currentUser.company.partner,
          currentUser: this.currentUser,
        }),
      })
      .subscribe((qs) => {
        this.questions = qs;
      });
  }

  goBack(): void {
    this.location.back();
  }

  onChange(event: AnyObject): void {
    const field: string = event.source?.id;

    // show or hide partner and company fields
    // TODO: when replacing the question, the callback of the replaced question is executed. We need to prevent it.

    if (field !== "type" && field !== "right") {
      return;
    }

    const type = event.value;

    if (type === UserTypes.Global) {
      // The partner and company fields must not be present
      this.dynamicForm.removeQuestion("partnerId");
      this.dynamicForm.removeQuestion("companyId");
    } else if (type === UserTypes.Partner) {
      // The company field must not be present
      this.dynamicForm.removeQuestion("companyId");
      // The partner field must be present
      if (!this.currentUser.isPartnerAdmin()) {
        this.dynamicForm.replaceOrAppendQuestion(this.questionsService.createPartnerDropdown());
      }
    } else if (type === UserTypes.Company) {
      // The partner and company fields must be present
      if (!this.currentUser.isPartnerAdminOrManager() && !this.currentUser.isCompanyAdmin()) {
        this.dynamicForm.replaceOrAppendQuestion(this.questionsService.createPartnerDropdown());
      }
      // The company field must be present
      if (!this.currentUser.isCompanyAdmin()) {
        this.dynamicForm.replaceOrAppendQuestion(
          this.questionsService.createCompanyDropdown({
            ...(this.currentUser.partnerId && { partnerId: this.currentUser.partnerId }),
          }),
        );
      }
    }

    if (
      this.dynamicForm.form.value.type === UserTypes.Partner &&
      this.dynamicForm.form.value.right === UserRights.Manager
    ) {
      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.createIsRepresentativeDropdown({ key: "isSalesRep", label: "Is Sales Rep" }),
      );
    } else {
      this.dynamicForm.removeQuestion("isSalesRep");
    }
    if (this.currentUser.isGlobalAdmin()) {
      if (
        this.dynamicForm.form.value.type === UserTypes.Global &&
        this.dynamicForm.form.value.right === UserRights.Manager
      ) {
        this.dynamicForm.replaceOrAppendQuestion(
          this.questionsService.createIsRepresentativeDropdown({ key: "isCWRep", label: "Is CW Rep" }),
        );
      } else {
        this.dynamicForm.removeQuestion("isCWRep");
      }
    }
    this.showIsFutureExpirationNotificationEnabled();
  }

  showIsFutureExpirationNotificationEnabled(): void {
    const currentUserIsAdminOrManager =
      this.currentUser.isGlobalAdminOrManager() || this.currentUser.isPartnerAdminOrManager();

    const isPartnerAdminOrManager =
      this.dynamicForm.form.value.type === UserTypes.Partner &&
      (this.dynamicForm.form.value.right === UserRights.Admin ||
        this.dynamicForm.form.value.right === UserRights.Manager);

    if (currentUserIsAdminOrManager) {
      if (isPartnerAdminOrManager) {
        this.dynamicForm.replaceOrAppendQuestion(
          this.questionsService.createIsFutureExpirationNotification({
            key: "isFutureExpirationNotificationEnabled",
            label: "Expiration Email Notifications",
          }),
        );
      } else {
        this.dynamicForm.removeQuestion("isFutureExpirationNotificationEnabled");
      }
    }
  }

  onSubmit(user: User): void {
    this.removeUndesiredFields(user);
    const data = this.getCleanedData(user);
    if (this.currentUser.isCompany()) {
      data.type = "company";
    }

    this.loader.show();
    this.userService
      .createUser(data)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (user: User) => {
          this.notificationService.success("User has been created");
          this.router.navigate([`/users/${user.id}`]);
        },
        (err) => this.notificationService.error(err),
      )
      .add(() => {
        this.loader.hide();
      });
  }

  private removeUndesiredFields(user: User): void {
    if (user.type === UserTypes.Partner) {
      delete user.companyId;
    } else if (user.type === UserTypes.Company && this.currentUser.isCompanyAdmin()) {
      delete user.companyId;
      // TODO: below condition has not sense... CHECK
    } else if (user.type === UserTypes.Company || this.currentUser.isPartnerAdminOrManager()) {
      delete user.partnerId;
    }
  }

  /**
   * Remove all properties with empty strings
   * @param user
   * @returns
   */
  private getCleanedData(user: User): AnyObject {
    return Object.fromEntries(Object.entries(user).filter(([_, v]) => v !== ""));
  }
}
